import { useMemo, useState } from 'react';
import { ChartFrame } from '../components/charts/ChartFrame';
import { BarSeries } from '../components/charts/BarSeries';
import { Delta, StatTile } from '../components/tiles/StatTile';
import { percent } from '../lib/format';
import { PLATFORM_COLORS, platformSeries } from '../lib/platform';
import { PLATFORM_LABELS, platformsFor } from '../data/types';
import type { FeatureRow, Platform, PlatformFilter, Snapshot } from '../data/types';

interface Props {
  snap: Snapshot;
  filter: PlatformFilter;
}

const CATEGORIES: (FeatureRow['category'] | 'All')[] = [
  'All',
  'Privacy',
  'Tabs',
  'AI',
  'Productivity',
  'Media',
  'Sync',
];

/**
 * Mean adoption across the platforms the filter covers. Platforms where the
 * feature does not exist are excluded from the mean rather than counted as
 * zero — a desktop-only feature is not "0% adopted" on iOS, it is absent.
 */
function adoptionFor(f: FeatureRow, platforms: Platform[]): number | null {
  const vals = platforms.map((p) => f.adoption[p]).filter((v): v is number => v !== null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

export function Features({ snap, filter }: Props) {
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('All');
  const series = platformSeries(filter);
  const active = platformsFor(filter);

  const rows = useMemo(
    () =>
      snap.features
        .filter((f) => category === 'All' || f.category === category)
        .filter((f) => adoptionFor(f, active) !== null)
        .sort((a, b) => (adoptionFor(b, active) ?? 0) - (adoptionFor(a, active) ?? 0)),
    [snap.features, category, active],
  );

  const chartData = rows.map((f) => {
    const row: Record<string, string | number> = { name: f.name };
    for (const p of active) {
      // A feature absent on a platform contributes 0 to that bar rather than
      // being dropped, so the row still lines up with its label. The table
      // below is where "n/a" and "0%" are told apart.
      row[p] = (f.adoption[p] ?? 0) * 100;
    }
    return row;
  });

  const withAdoption = snap.features
    .map((f) => ({ f, a: adoptionFor(f, active) }))
    .filter((x): x is { f: FeatureRow; a: number } => x.a !== null);

  const top = [...withAdoption].sort((a, b) => b.a - a.a)[0];
  const bottom = [...withAdoption].sort((a, b) => a.a - b.a)[0];
  const rising = [...snap.features].sort((a, b) => b.trend - a.trend)[0];
  const meanAdoption = withAdoption.reduce((s, x) => s + x.a, 0) / (withAdoption.length || 1);

  return (
    <>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
        <StatTile label="Features tracked" value={String(snap.features.length)} foot="across both clients" />
        <StatTile label="Mean adoption" value={percent(meanAdoption)} foot="of active users" />
        <StatTile label="Most used" value={percent(top.a)} foot={top.f.name} sparkColor="var(--status-good)" />
        <StatTile
          label="Least used"
          value={percent(bottom.a)}
          foot={bottom.f.name}
          sparkColor="var(--status-serious)"
        />
      </div>

      <div className="grid">
        <div data-span style={{ gridColumn: 'span 12' }}>
          <ChartFrame
            title="Feature adoption"
            subtitle="Share of each platform's active users who used the feature at least once in the range"
            tools={
              <div className="segmented" role="group" aria-label="Category">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="segmented__btn"
                    aria-pressed={category === c}
                    onClick={() => setCategory(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
            }
            table={{
              columns: [
                'Feature',
                'Category',
                ...active.map((p) => PLATFORM_LABELS[p]),
                'Uses / wk',
                'Trend',
              ],
              rows: rows.map((f) => [
                f.name,
                f.category,
                ...active.map((p) => (f.adoption[p] === null ? 'n/a' : percent(f.adoption[p]!))),
                f.frequency.toFixed(1),
                `${f.trend > 0 ? '+' : ''}${(f.trend * 100).toFixed(1)}%`,
              ]),
            }}
          >
            <BarSeries
              data={chartData}
              categoryKey="name"
              series={series}
              layout="horizontal"
              height={Math.max(280, chartData.length * (active.length > 2 ? 46 : 30))}
              categoryWidth={190}
              valueFormat={(n) => `${n.toFixed(0)}%`}
            />
          </ChartFrame>
        </div>

        <div data-span style={{ gridColumn: 'span 12' }}>
          <section className="card">
            <header className="card__head">
              <div className="card__titles">
                <h2 className="card__title">Feature detail</h2>
                <p className="card__sub">
                  Fastest riser this range: <b style={{ color: 'var(--text-primary)' }}>{rising.name}</b>
                </p>
              </div>
            </header>
            <div className="card__body">
              <div className="table-scroll" style={{ maxHeight: 520 }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th scope="col">Feature</th>
                      <th scope="col" style={{ textAlign: 'left' }}>
                        Category
                      </th>
                      {active.map((p) => (
                        <th scope="col" key={p}>
                          {PLATFORM_LABELS[p]}
                        </th>
                      ))}
                      <th scope="col">Uses / wk</th>
                      <th scope="col">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((f) => (
                      <tr key={f.id}>
                        <th scope="row" style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                          {f.name}
                        </th>
                        <td style={{ textAlign: 'left' }}>
                          <span className="chip">{f.category}</span>
                        </td>
                        {active.map((p) => (
                          <td key={p}>
                            {f.adoption[p] === null ? (
                              <span className="muted" title="Not available on this platform">
                                n/a
                              </span>
                            ) : (
                              <AdoptionCell value={f.adoption[p]!} color={PLATFORM_COLORS[p]} />
                            )}
                          </td>
                        ))}
                        <td>{f.frequency.toFixed(1)}</td>
                        <td>
                          <Delta value={f.trend} suffix="" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function AdoptionCell({ value, color }: { value: number; color: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
      <span className="bar-track" style={{ width: 44 }}>
        <span className="bar-fill" style={{ width: `${value * 100}%`, background: color, display: 'block' }} />
      </span>
      {percent(value, 0)}
    </span>
  );
}
